import { apiJson } from '../api';
import { getAccessToken } from '../auth';
import { sendTelemetry, telemetryAccessToken } from '../utils/readerTelemetry';
import type { StoryElement } from '../types';
import { INTERACTION_EVENT_TYPES } from '../utils/userEvents';
import type { UserEventType } from '../utils/userEvents';

export interface UserEventContext {
  surface: 'tldr' | 'search' | 'channel' | 'article' | 'topics' | 'for_you' | 'default' | 'feed' | 'unknown';
  surfaceId?: string | null;
  properties?: Record<string, unknown>;
  destinationUrl?: string | null;
}

export interface StoryPermalinkResponse {
  permalink_id: string;
  story: StoryElement;
}

export async function getStoryPermalink(identifier: string): Promise<StoryPermalinkResponse> {
  return apiJson<StoryPermalinkResponse>(
    `/api/story-groups/${encodeURIComponent(identifier)}`,
    { public: true },
  );
}

/**
 * Topic siblings for a story whose surface did not ship them inline. Fetched
 * when a reader expands a Fast Search row (issue #837) rather than for every
 * result, so the cost lands only on rows people actually open.
 */
export async function getStoryAlternates(
  storyId: string,
): Promise<NonNullable<StoryElement['alternatives']>> {
  const data = await apiJson<{ alternates?: StoryElement['alternatives'] }>(
    `/api/stories/${encodeURIComponent(storyId)}/alternates`,
    { public: !getAccessToken() },
  );
  return data.alternates || [];
}

/**
 * A story's context bullets, which reader lists defer (`has_topic_context`): read
 * when a reader opens the row, as its alternates are.
 */
export async function getStoryContext(
  storyId: string,
): Promise<NonNullable<StoryElement['topic_context']>> {
  const data = await apiJson<{ topic_context: NonNullable<StoryElement['topic_context']> }>(
    `/api/stories/${encodeURIComponent(storyId)}/context`,
    { public: !getAccessToken() },
  );
  return data.topic_context;
}

export interface HydratedStoryScoreDetails {
  pangram?: StoryElement['pangram'];
  category?: string;
  base_score_details: StoryElement['base_score_details'];
  flagged_as_ai?: boolean | null;
  date_provenance?: StoryElement['date_provenance'];
}

/**
 * A story's score-breakdown blob and date provenance, read by the admin article
 * page (/article/admin/:id). The reader list defers the blob: it is admin-only detail, and
 * shipping it with every row cost a quarter of the feed payload.
 */
export async function getStoryScoreDetails(
  storyId: string,
): Promise<HydratedStoryScoreDetails> {
  const data = await apiJson<{
    pangram?: StoryElement['pangram'];
    category?: string;
    base_score_details?: StoryElement['base_score_details'];
    flagged_as_ai?: boolean | null;
    date_provenance?: StoryElement['date_provenance'];
  }>(
    `/api/stories/${encodeURIComponent(storyId)}/score-details`,
  );
  return {
    pangram: data.pangram,
    category: data.category,
    base_score_details: data.base_score_details ?? null,
    flagged_as_ai: data.flagged_as_ai,
    date_provenance: data.date_provenance,
  };
}

/**
 * Status events remain per-account. Anonymous readers send only interaction
 * telemetry through the constrained public endpoint, which stores no user id.
 */
export async function recordEvent(
  storyId: string,
  eventType: UserEventType,
  context: UserEventContext = { surface: 'unknown' },
): Promise<void> {
  if ((INTERACTION_EVENT_TYPES as readonly string[]).includes(eventType)) {
    // Storage/token failures are caught at this best-effort boundary too.
    try {
      const base = telemetryAccessToken() ? '/api/stories' : '/api/public/stories';
      sendTelemetry(`${base}/${encodeURIComponent(storyId)}/events`, {
        event_type: eventType, surface: context.surface, surface_id: context.surfaceId || null,
        destination_url: context.destinationUrl || null, properties: context.properties || {},
      });
    } catch { /* telemetry never affects the reader */ }
    return;
  }
  if (!getAccessToken()) return;
  await apiJson(`/api/stories/${encodeURIComponent(storyId)}/events`, {
    method: 'POST', json: { event_type: eventType, surface: context.surface,
      surface_id: context.surfaceId || null, destination_url: context.destinationUrl || null,
      properties: context.properties || {} },
  });
}
